<template>
  <div class="mb-8 mb-lg-4">
    <v-row class="text-center mt-0 mt-md-4 mb-2" no-gutters data-cy="clientZoneDashboardProfile">
      <v-col cols="12 d-flex justify-center" data-cy="clientZoneDashboardProfilePicture">
        <ProfilePicture show-badge :listen-to-click="listenToClick" @click="goToAccountSettings" />
      </v-col>
      <v-col cols="12" class="mt-4" data-cy="clientZoneDashboardProfileName">
        <div class="d-flex align-center justify-center">
          <div class="text-h1">{{ $auth.user.firstName }} {{ $auth.user.lastName }}</div>
        </div>
      </v-col>
    </v-row>
    <div class="mb-6">
      <div
        v-if="isPending"
        class="text-center text-body-small grey-darken-1--text"
        data-cy="clientZoneDashboardBenefitProvider"
      >
        {{ $t('dashboard.requestSent') }}
        <v-btn
          block
          small
          :class="{
            'bg-warning': isInWarningStateOfBenefit,
            'bg-error': isInErrorStateOfBenefit,
            'bg-primary': !isInWarningStateOfBenefit && !isInErrorStateOfBenefit
          }"
          class="custom-btn mt-3"
          @click="goToBenefitManegment"
        >
          {{ $t(`profile.benefit.${profileButtonCase}.buttonText`) }}
        </v-btn>
      </div>
      <template v-else>
        <div
          class="text-center text-body-small grey-darken-1--text"
          data-cy="clientZoneBenefitIsProvidedBy"
        >
          <!-- 1) Host case (exclusive) -->
          <template v-if="$auth.user.invitation?.host">
            {{
              $t('profileSection.thankYouHost', {
                host: invitationHostLangValue
              })
            }}
          </template>

          <!-- 2) Partly covered price (exclusive) -->
          <template
            v-else-if="priceNotFullyCovered && $auth.user.benefit?.active && partnerData?.id === 1"
          >
            <!-- when expanding to other partners, drop the partner.id === 1 condition -->
            {{
              $t('profileSection.thankYouPartly', {
                partnerName: partnerData?.name
              })
            }}
            <br />
            {{ $t('payment.priceAfterDiscount', { price: currentMutation.questionPrice }) }}
          </template>

          <!-- 3) Active benefit, no host (exclusive) -->
          <template v-else-if="$auth.user.benefit?.active && !$auth.user.invitation?.host">
            {{ $t('profileSection.thankYou', { partnerName: partnerData?.name }) }}
          </template>

          <!-- 4) Self pay (fallback) -->
          <template v-else>
            {{ $t('profileSection.thankYouSelf') }}
          </template>

          <div
            v-if="!isB2C && isReauthorizationAllowed"
            class="font-weight-bold"
            :class="{
              'primary--text': isInPrimaryStateOfBenefit,
              'warning--benefit': isInWarningStateOfBenefit
            }"
          >
            {{ $t('profile.benefit.validTo') }}:&nbsp;{{ benefitValidTo }}
          </div>

          <template>
            <v-btn
              block
              small
              :class="{
                'bg-warning': isInWarningStateOfBenefit,
                'bg-error': isInErrorStateOfBenefit,
                'bg-primary':
                  (isInPrimaryStateOfBenefit ||
                    ($auth.user.benefit?.active && !isInWarningStateOfBenefit)) &&
                  !isInWarningStateOfBenefit &&
                  !isInErrorStateOfBenefit
              }"
              class="custom-btn mt-3"
              @click="goToBenefitManegment"
            >
              {{ $t(`profile.benefit.${profileButtonCase}.buttonText`) }}
            </v-btn>
          </template>
        </div>
      </template>
    </div>
    <template v-if="!xxlOnly">
      <CarouselHolder
        v-if="!$auth.user.benefit.active && showNewsletter"
        pagination
        :arrows="false"
        align="center"
        :pagination-classes="['pt-2', 'd-flex']"
        class="mb-6"
        :fade="false"
      >
        <div key="authorization-dialog" class="profile_data_carousel_box">
          <DashboardBox
            v-if="!$auth.user.benefit.active"
            class="mr-1"
            icon="care"
            :title="$t('profileSection.freeService.title')"
            :text="$t('profileSection.freeService.text')"
            :button-text="$t('profileSection.freeService.button')"
            @clickButton="openAuthorizationModal"
          />
        </div>
        <div key="newsletter-dialog" class="profile_data_carousel_box">
          <DashboardBox
            v-if="showNewsletter"
            class="ml-1"
            icon="mail-empty"
            :title="$t('newsletterAgreement.dashboard.title')"
            :text="$t('newsletterAgreement.dashboard.text')"
            :button-text="$t('subscribe')"
            @clickButton="setDialog('newsletter-agreement')"
          />
        </div>
      </CarouselHolder>
      <DashboardBox
        v-else-if="!$auth.user.benefit.active"
        icon="care"
        :title="$t('profileSection.freeService.title')"
        :text="$t('profileSection.freeService.text')"
        :button-text="$t('profileSection.freeService.button')"
        @clickButton="openAuthorizationModal"
      />
      <DashboardBox
        v-else-if="showNewsletter"
        icon="mail-empty"
        :title="$t('newsletterAgreement.dashboard.title')"
        :text="$t('newsletterAgreement.dashboard.text')"
        :button-text="$t('subscribe')"
        @clickButton="setDialog('newsletter-agreement')"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import dashboardBoxes from '~/mixins/dashboardBoxes';
import breakpoints from '~/mixins/breakpoints';
import reauthorization from '~/mixins/reauthorization';
import partnerDataCurrentMutation from '~/mixins/currentMutation/partnerDataCurrentMutation';
import handleBenefitCases from '~/mixins/handleBenefitCases.js';
import handleBenefitViews from '~/mixins/handleBenefitViews.js';

export default {
  name: 'ProfileData',
  mixins: [
    dashboardBoxes,
    breakpoints,
    reauthorization,
    partnerDataCurrentMutation,
    reauthorization,
    handleBenefitCases,
    handleBenefitViews
  ],
  props: {
    listenToClick: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      shouldSeeReauthorization: 'profile/reauthorization/shouldSeeReauthorization'
    }),
    invitationHostLangValue() {
      const authHost = this.$auth.user.invitation.host;
      if (!authHost) {
        return null;
      }

      if (!authHost.firstName && !authHost.lastName) {
        return authHost.email;
      }

      return ((authHost.firstName || '') + ' ' + (authHost.lastName || '')).trim();
    }
  },
  methods: {
    ...mapMutations({
      setDialog: 'profile/dashboard/setDialog',
      setAuthorizationType: 'profile/authorization/setAuthorizationType'
    }),
    openAuthorizationModal() {
      if (this.$auth.user.benefit.status === 'pending') {
        this.setAuthorizationType('basic');
      }

      this.$router.push(this.$localePath('benefitManagement'));
    },
    goToAccountSettings() {
      const path = this.$localePath({
        name: 'accountSettings',
        language: this.$route.meta.languageKey
      });
      this.$router.push(path);
    },
    goToBenefitManegment() {
      this.$router.push(this.$localePath({ name: 'benefitManagement' }));
    }
  }
};
</script>

<style lang="scss" scoped>
.profile_data_carousel_box {
  width: 90%;
}
.bg-warning {
  background-color: #e68c14 !important;
}
.bg-error {
  background-color: #ff5050 !important;
}
.bg-primary {
  background-color: #3ca091 !important;
}
.custom-btn {
  color: #fff !important;
  border-radius: 45px;
  gap: 8px;
  &:hover,
  &:active,
  &:focus {
    &:before {
      opacity: 0;
    }
  }
}
.warning-benefit {
  color: #e68c14;
}
</style>
